import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Store, Minus, Plus, Trash2, CheckCircle2, Loader2, Send, ArrowRight, Bitcoin } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { useCart } from "@/cart/CartContext";
import { useAuth } from "@/auth/AuthContext";
import { useContent } from "@/hooks/useContent";
import { warehouseTag, warehouseName } from "@/utils/warehouse";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRYPTO_TYPES = ["btc", "eth", "usdt"];

// Field-card hint shown the moment a buyer picks a crypto payment rail at checkout
const CryptoRouteHint = ({ testid }) => (
  <div data-testid={testid} className="border border-clinical/30 bg-[#0B101C] rounded-xl px-4 py-3.5 space-y-2.5">
    <p className="text-[10px] uppercase tracking-[0.25em] text-clinical flex items-center gap-2">
      <Bitcoin className="h-3.5 w-3.5" strokeWidth={1.5} /> Field card — Crypto 1-2-3
    </p>
    <ol className="text-[11px] text-[#B9BFCE] leading-relaxed space-y-1 font-mono">
      <li>01 — Buy the coin above + $5 extra for gas (SOL or ETH)</li>
      <li>02 — Move it into your Trust Wallet</li>
      <li>03 — Send to the address above FROM Trust Wallet</li>
    </ol>
    <p className="text-[10px] text-[#8E96AB] leading-relaxed">
      Never send direct from CashApp, Venmo, Zelle, or Coinbase.{" "}
      <Link to="/crypto-briefing" className="text-clinical underline underline-offset-4 hover:text-ink transition-colors duration-300">Read the full Crypto Briefing</Link>
    </p>
  </div>
);

const EMPTY_FORM = {
  first_name: "", last_name: "", phone: "", email: "",
  street: "", city: "", state: "", zip_code: "",
};

export default function Cart() {
  const { items, updateQty, remove, clear, count, add, discount, applyDiscount, removeDiscount } = useCart();
  const { user, token, membership } = useAuth();
  const isMember = membership?.membership === "active";
  const content = useContent();
  const COPY = {
    overline: "Checkout",
    title1: "Review &",
    title2: "submit.",
    empty_overline: "Cart",
    empty_title: "Your cart is empty",
    empty_text: "Add compounds from the catalog to begin an order inquiry.",
    empty_button: "Browse the catalog",
    confirmation_overline: "Order inquiry received",
    confirmation_note: "Your order and stock availability will be confirmed by VetForge before any payment is requested.",
    payment_note: "Submitting sends your order inquiry directly to VetForge. Your order and stock availability will be confirmed by VetForge before any payment is requested.",
    payment_label: "VetForge accepts",
    deadline_note: "Payment must be submitted within 48 hours of receiving invoice confirmation",
    shipping_note: "Shipping cost confirmed by VetForge",
    ...(content?.cart_page || {}),
  };
  const RUO = content?.footer?.ruo || "Research use only — Not for human consumption";
  const [form, setForm] = useState(EMPTY_FORM);
  const [shipping, setShipping] = useState("FedEx");
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [codeInput, setCodeInput] = useState("");
  const [codeError, setCodeError] = useState("");

  const applyCode = async () => {
    setCodeError("");
    try {
      const { data } = await axios.post(`${API}/discounts/validate`, { code: codeInput }, { headers: { Authorization: `Bearer ${token}` } });
      applyDiscount({ code: data.code, percent: data.percent });
      setCodeInput("");
      toast.success(`${data.code} applied — ${data.percent}% off`);
    } catch {
      removeDiscount();
      setCodeError("Invalid or expired code.");
    }
  };
  const [catalog, setCatalog] = useState({});
  const [popularity, setPopularity] = useState({});
  const [paymentMethod, setPaymentMethod] = useState("manual");
  const [squareInfo, setSquareInfo] = useState({ enabled: false, fee_percent: 3.75 });
  useEffect(() => {
    axios.get(`${API}/products`).then(({ data }) => setCatalog(Object.fromEntries(data.map((d) => [d.id, d])))).catch(() => {});
    axios.get(`${API}/products/popularity`).then(({ data }) => setPopularity(data)).catch(() => {});
    axios.get(`${API}/settings/square`).then(({ data }) => setSquareInfo(data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        email: f.email || user.email,
        first_name: f.first_name || user.name.split(" ")[0] || "",
        last_name: f.last_name || user.name.split(" ").slice(1).join(" ") || "",
        phone: f.phone || user.profile?.phone || "",
        street: f.street || user.profile?.street || "",
        city: f.city || user.profile?.city || "",
        state: f.state || user.profile?.state || "",
        zip_code: f.zip_code || user.profile?.zip_code || "",
      }));
    }
  }, [user]);
  const liveItems = items.map((i) => {
    const cat = catalog[i.product_id || i.id];
    const variant = (cat?.variants || []).find((x) => x.id === i.variant_id);
    // Member price applies only with an active paid membership — free accounts pay listed price
    const effPrice = variant
      ? ((isMember ? variant.member_price : null) ?? variant.price)
      : ((isMember ? cat?.member_price : null) ?? cat?.price);
    return { ...i, price: effPrice ?? i.price, name: cat?.name ?? i.name, vendorId: cat?.vendor_id || null, vendorName: cat?.vendor_name || i.vendor_name || "VetForge Supply" };
  });
  // What a paid member WOULD save on this cart — upsell teaser for free accounts
  const memberSavings = liveItems.reduce((sum, i) => {
    const cat = catalog[i.product_id || i.id];
    const variant = (cat?.variants || []).find((x) => x.id === i.variant_id);
    const mp = variant ? variant.member_price : cat?.member_price;
    return mp != null && mp < i.price ? sum + (i.price - mp) * i.quantity : sum;
  }, 0);
  // Group by store ID (not name) so distinct stores never collide
  const vendorGroups = liveItems.reduce((acc, item) => {
    const key = item.vendorId || "house";
    (acc[key] = acc[key] || { name: item.vendorName, items: [] }).items.push(item);
    return acc;
  }, {});
  const multiVendor = Object.keys(vendorGroups).length > 1;
  const hasHouseItems = !!vendorGroups.house;

  // Each store has its own payment rails — fetched per store, never shared across stores
  const [vendorOptions, setVendorOptions] = useState({});
  const [paymentChoices, setPaymentChoices] = useState({});
  const vendorKeys = Object.keys(vendorGroups).sort().join(",");
  useEffect(() => {
    Object.keys(vendorGroups).forEach((key) => {
      if (vendorOptions[key]) return;
      axios.get(`${API}/vendors/${key}/payment-options`).then(({ data }) => {
        setVendorOptions((prev) => ({ ...prev, [key]: data.options || [] }));
        if ((data.options || []).length === 1) {
          setPaymentChoices((prev) => ({ ...prev, [key]: data.options[0].type }));
        }
      }).catch(() => setVendorOptions((prev) => ({ ...prev, [key]: [] })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorKeys]);
  const total = liveItems.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const discountedTotal = discount ? total * (1 - discount.percent / 100) : total;
  // Shipping estimate per store — mirrors the server rule (server recomputes authoritatively):
  // US warehouse base $20 (or the store's charge); monthly members 25% off everywhere;
  // annual members free US warehouse + 35% off every other warehouse.
  const memberPlan = isMember ? membership?.plan : null;
  const estimateShipping = (groupItems) => {
    const warehouses = groupItems.map((i) => catalog[i.product_id || i.id]?.warehouse || "us");
    const wh = warehouses.every((w) => w === "us") ? "us" : warehouses.find((w) => w !== "us");
    const base = Math.max(...groupItems.map((i) => catalog[i.product_id || i.id]?.shipping_charge ?? 20));
    if (!memberPlan) return { base, cost: base, perk: null, wh };
    if (wh === "us") {
      if (memberPlan === "annual") return { base, cost: 0, perk: "Free US warehouse shipping — annual member", wh };
      return { base, cost: Math.round(base * 75) / 100, perk: "25% off US warehouse shipping — monthly member", wh };
    }
    if (memberPlan === "annual") return { base, cost: Math.round(base * 65) / 100, perk: `35% off ${wh} warehouse shipping — annual member`, wh };
    return { base, cost: Math.round(base * 75) / 100, perk: `25% off ${wh} warehouse shipping — monthly member`, wh };
  };
  const shippingByGroup = Object.fromEntries(Object.entries(vendorGroups).map(([k, g]) => [k, estimateShipping(g.items)]));
  const shippingTotal = Object.values(shippingByGroup).reduce((s, g) => s + g.cost, 0);
  // The PayPal/card processing fee applies ONLY to the house store's share — vendor rails are direct, no fee
  const houseSubtotal = (vendorGroups.house?.items || []).reduce((sum, i) => sum + i.price * i.quantity, 0);
  const houseDiscounted = discount ? houseSubtotal * (1 - discount.percent / 100) : houseSubtotal;
  const processingFee = paymentMethod !== "manual" && hasHouseItems ? Math.round((houseDiscounted + (shippingByGroup.house?.cost || 0)) * squareInfo.fee_percent) / 100 : 0;
  const checkoutTotal = discountedTotal + shippingTotal + processingFee;
  // Surface popular compounds the shopper hasn't added yet
  const recommendations = Object.values(catalog)
    .filter((p) => !items.some((i) => (i.product_id || i.id) === p.id))
    .sort((a, b) => (popularity[b.id] || 0) - (popularity[a.id] || 0))
    .slice(0, 3);

  const recAdd = (p) => {
    const v = (p.variants || [])[0] || null;
    add({ id: p.id, name: p.name, price: p.price, image: p.image, size: p.size }, v);
    toast.success(`${p.name}${v ? ` — ${v.label}` : ""} added to cart`);
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const required = ["first_name", "last_name", "phone", "email", "street", "city", "state", "zip_code"];
    if (required.some((k) => !form[k].trim())) {
      toast.error("Please complete all checkout fields.");
      return;
    }
    // Every store that configured payment rails needs its own selection — carts never share one checkout
    const missing = Object.entries(vendorGroups).filter(([k]) => k !== "house" && (vendorOptions[k] || []).length > 0 && !paymentChoices[k]);
    if (missing.length) {
      toast.error(`Choose a payment option for ${missing.map(([, g]) => g.name).join(", ")}.`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        shipping_method: shipping,
        discount_code: discount?.code || null,
        payment_method: paymentMethod,
        payment_choices: paymentChoices,
        items: liveItems.map((i) => ({ id: i.product_id || i.id, variant_id: i.variant_id || null, name: i.name, size: i.size, quantity: i.quantity, price: i.price })),
      };
      const { data } = await axios.post(`${API}/orders`, payload, { headers: { Authorization: `Bearer ${token}` } });
      const orders = data.orders || [data];
      if (paymentMethod === "square" && orders.length === 1) {
        try {
          const { data: checkout } = await axios.post(`${API}/orders/${orders[0].id}/square-checkout`, {}, { headers: { Authorization: `Bearer ${token}` } });
          if (!checkout?.checkout_url) throw new Error("no checkout url");
          clear();
          toast.success("Order placed — redirecting to secure card checkout…");
          window.location.assign(checkout.checkout_url);
          return;
        } catch {
          toast.error("Card checkout is unavailable right now — your order is saved; you can pay by card anytime from My Orders.");
        }
      }
      if (paymentMethod === "square" && orders.length > 1) {
        toast.success(`${orders.length} store orders placed — pay each one by card from My Orders.`);
      }
      setConfirmation({ ...data, orders });
      clear();
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error("Order submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <div data-testid="order-confirmation" className="mx-auto max-w-3xl px-6 sm:px-10 pt-24 pb-40 lg:pt-32 text-center space-y-10">
        <FadeUp className="space-y-8">
          <CheckCircle2 className="mx-auto h-14 w-14 text-clinical" strokeWidth={1.25} />
          <div className="space-y-4">
            <p className="text-xs uppercase tracking-[0.35em] text-clinical">{COPY.confirmation_overline}</p>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter text-ink leading-none">
              Thank you, <span className="italic text-[#B9BFCE]">{confirmation.orders[0].first_name}.</span>
            </h1>
          </div>
          <div className="border border-[#232B40] bg-paper p-8 sm:p-10 space-y-5 text-left">
            {(confirmation.orders || [confirmation]).length > 1 ? (
              <>
                <p className="text-sm text-ink leading-relaxed" data-testid="confirmation-split-note">
                  Your cart spanned multiple stores, so it was split into <span className="text-clinical">{confirmation.orders.length} separate orders</span> — each store confirms, ships, and tracks its part independently:
                </p>
                <div className="space-y-2">
                  {confirmation.orders.map((o) => (
                    <div key={o.id} className="border border-[#232B40] rounded-xl px-4 py-3 text-xs space-y-2" data-testid={`confirmation-order-${o.id.slice(0, 8)}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-ink">{o.vendor_name} <span className="text-[#8E96AB]">— order {o.order_number || o.id.slice(0, 8).toUpperCase()}</span></span>
                        <span className="text-clinical font-semibold">${o.total.toFixed(2)}</span>
                      </div>
                      {o.payment_option && (
                        <div className="flex items-center gap-3 border border-[rgba(200,169,106,0.35)] bg-[#0B101C] rounded-lg px-3 py-2" data-testid={`confirmation-pay-${o.id.slice(0, 8)}`}>
                          <span className="text-[10px] uppercase tracking-[0.15em] text-[#8E96AB] shrink-0">Pay via {o.payment_option.label}:</span>
                          <span className="text-clinical font-mono break-all flex-1">{o.payment_option.handle}</span>
                          <button type="button" data-testid={`confirmation-copy-${o.id.slice(0, 8)}`} onClick={() => { navigator.clipboard?.writeText(o.payment_option.handle); toast.success("Payment address copied."); }} className="text-[10px] uppercase tracking-[0.15em] text-[#8E96AB] hover:text-clinical border border-[#232B40] rounded-lg px-2.5 py-1 transition-colors duration-300 shrink-0">Copy</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-ink leading-relaxed">
                Your order inquiry <span className="text-clinical">{confirmation.orders[0].id.slice(0, 8).toUpperCase()}</span> ({confirmation.orders[0].items.length} item{confirmation.orders[0].items.length !== 1 ? "s" : ""}, total ${confirmation.orders[0].total.toFixed(2)}, shipping via {confirmation.orders[0].shipping_method}) has been sent to {confirmation.orders[0].vendor_name}.
              </p>
            )}
            {confirmation.orders[0].payment_option && (
              <div className="flex items-center gap-3 border border-[rgba(200,169,106,0.35)] bg-[#0B101C] rounded-xl px-4 py-3" data-testid="confirmation-pay-single">
                <span className="text-[10px] uppercase tracking-[0.15em] text-[#8E96AB] shrink-0">Pay ${confirmation.orders[0].total.toFixed(2)} via {confirmation.orders[0].payment_option.label}:</span>
                <span className="text-clinical font-mono break-all flex-1">{confirmation.orders[0].payment_option.handle}</span>
                <button type="button" data-testid="confirmation-copy-single" onClick={() => { navigator.clipboard?.writeText(confirmation.orders[0].payment_option.handle); toast.success("Payment address copied."); }} className="text-[10px] uppercase tracking-[0.15em] text-[#8E96AB] hover:text-clinical border border-[#232B40] rounded-lg px-2.5 py-1 transition-colors duration-300 shrink-0">Copy</button>
              </div>
            )}
            <p className="text-sm text-[#B9BFCE] leading-relaxed" data-testid="order-notice">
              {COPY.confirmation_note}{" "}
              We will reach you at <span className="text-ink">{confirmation.orders[0].email}</span> with confirmation and payment instructions.
            </p>
            <p className="text-[11px] text-[#8E96AB] leading-relaxed border-t border-[#232B40] pt-4" data-testid="confirmation-policy-note">
              <span className="uppercase tracking-[0.15em] text-ink">All sales are final.</span> A 3.75% processing fee applies to PayPal and all card payments. It is the goal of VetForge Health and Wellness to provide a world class experience — if we do not meet those expectations with an order issue, please allow us the opportunity to reconcile the situation.
            </p>
            {confirmation.orders[0].payment_method === "square" && confirmation.orders[0].payment_status !== "paid" && (
              <Link
                to="/orders"
                data-testid="confirmation-pay-link"
                className="inline-flex items-center gap-3 bg-clinical text-[#0B101C] text-xs uppercase tracking-[0.2em] py-3 px-6 hover:bg-[#DBC28C] transition-colors duration-300"
              >
                Pay by card from My Orders
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
            <p className="text-[10px] uppercase tracking-[0.25em] text-warning">
              {RUO}
            </p>
          </div>
          <Link
            to="/products"
            data-testid="confirmation-continue-link"
            className="group inline-flex items-center gap-3 border border-[rgba(200,169,106,0.28)] text-ink text-xs uppercase tracking-[0.2em] py-4 px-8 hover:bg-[#1A233A] transition-colors duration-300"
          >
            Continue browsing
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div data-testid="empty-cart-message" className="mx-auto max-w-3xl px-6 sm:px-10 pt-24 pb-40 lg:pt-32 text-center space-y-8">
        <FadeUp className="space-y-8">
          <p className="text-xs uppercase tracking-[0.35em] text-clinical">{COPY.empty_overline}</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter text-ink leading-none">{COPY.empty_title}</h1>
          <p className="text-sm text-[#B9BFCE]">{COPY.empty_text}</p>
          <Link
            to="/products"
            data-testid="cart-products-link"
            className="group inline-flex items-center gap-3 bg-clinical text-[#0B101C] text-xs uppercase tracking-[0.2em] py-4 px-8 hover:bg-[#DBC28C] transition-colors duration-300"
          >
            {COPY.empty_button}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="mx-auto max-w-7xl px-6 sm:px-10 pt-24 pb-32 lg:pt-32">
      <FadeUp className="mb-16 space-y-4">
        <p className="text-xs uppercase tracking-[0.35em] text-clinical">{COPY.overline} — {count} item{count !== 1 ? "s" : ""}</p>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tighter text-ink leading-none">
          {COPY.title1} <span className="italic text-[#B9BFCE]">{COPY.title2}</span>
        </h1>
      </FadeUp>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5 space-y-px bg-[#232B40] border border-[#232B40] self-start">
          {Object.entries(vendorGroups).map(([vKey, group]) => (
            <div key={vKey}>
              {multiVendor && (
                <div className="bg-surface px-6 py-3 flex items-center gap-2" data-testid={`cart-store-${group.name.replace(/\W+/g, "-").toLowerCase()}`}>
                  <Store className="h-3.5 w-3.5 text-clinical" />
                  <span className="text-[10px] uppercase tracking-[0.25em] text-clinical">{group.name}</span>
                  <span className="text-[10px] text-[#8E96AB] ml-auto">ships separately</span>
                </div>
              )}
              {group.items.map((item) => (
            <div key={item.id} className="bg-paper p-6 flex gap-6 items-center" data-testid={`cart-item-${item.id}`}>
              <div className="relative shrink-0">
                <img src={item.image} alt={item.name} onError={(e) => { e.currentTarget.src = "/images/hero-vials.png"; }} className="w-20 h-20 object-cover border border-[#232B40]" />
              </div>
              <div className="flex-1 space-y-1">
                <p className="font-serif text-xl text-ink">{item.name}</p>
                <p className="text-xs text-[#8E96AB] uppercase tracking-[0.15em]">{item.size}</p>
                <p className="text-sm text-clinical flex items-center gap-2">
                  ${item.price.toFixed(2)} / vial
                  <span
                    className="font-sans text-[9px] uppercase tracking-[0.1em] text-[#8E96AB] border border-[#232B40] rounded px-1.5 py-0.5"
                    title={`Ships from ${warehouseName(catalog[item.product_id || item.id]?.warehouse)}`}
                    data-testid={`warehouse-tag-${item.id}`}
                  >
                    {warehouseTag(catalog[item.product_id || item.id]?.warehouse)}
                  </span>
                </p>
                <div className="flex items-center gap-3 pt-2">
                  <button data-testid={`qty-minus-${item.id}`} onClick={() => updateQty(item.id, -1)} className="border border-[rgba(200,169,106,0.28)] p-1.5 hover:bg-[#1A233A] transition-colors duration-300" aria-label="Decrease quantity">
                    <Minus className="h-3 w-3" />
                  </button>
                  <span className="text-sm w-6 text-center" data-testid={`qty-value-${item.id}`}>{item.quantity}</span>
                  <button data-testid={`qty-plus-${item.id}`} onClick={() => updateQty(item.id, 1)} className="border border-[rgba(200,169,106,0.28)] p-1.5 hover:bg-[#1A233A] transition-colors duration-300" aria-label="Increase quantity">
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
              </div>
              <button data-testid={`remove-${item.id}`} onClick={() => remove(item.id)} className="text-[#8E96AB] hover:text-red-700 transition-colors duration-300 p-2" aria-label="Remove item">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
              ))}
            </div>
          ))}
          <div className="bg-paper p-6 space-y-3" data-testid="order-total-row">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.25em] text-[#8E96AB]">Subtotal</span>
              <span className="text-sm text-[#B9BFCE]">${total.toFixed(2)}</span>
            </div>
            {discount && (
              <div className="flex items-center justify-between" data-testid="discount-row">
                <span className="text-xs uppercase tracking-[0.25em] text-clinical">{discount.code} — {discount.percent}% off</span>
                <span className="text-sm text-clinical">−${(total * discount.percent / 100).toFixed(2)}</span>
              </div>
            )}
            {Object.entries(shippingByGroup).map(([k, g]) => (
              <div key={k} className="space-y-0.5" data-testid={`shipping-row-${k === "house" ? "house" : k.slice(0, 8)}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-[0.25em] text-[#8E96AB]">
                    Shipping{Object.keys(shippingByGroup).length > 1 ? ` — ${vendorGroups[k].name}` : ""} ({g.wh === "us" ? "US warehouse" : `${g.wh} warehouse`})
                  </span>
                  <span className={`text-sm ${g.cost === 0 ? "text-clinical" : "text-[#B9BFCE]"}`}>
                    {g.cost === 0 ? "FREE" : `+$${g.cost.toFixed(2)}`}
                  </span>
                </div>
                {g.perk && (
                  <p className="text-[10px] uppercase tracking-[0.15em] text-clinical" data-testid={`shipping-perk-${k === "house" ? "house" : k.slice(0, 8)}`}>{g.perk} (was ${g.base.toFixed(2)})</p>
                )}
              </div>
            ))}
            {paymentMethod !== "manual" && (
              <div className="flex items-center justify-between" data-testid="processing-fee-row">
                <span className="text-xs uppercase tracking-[0.25em] text-[#8E96AB]">{paymentMethod === "square" ? "Card" : "PayPal"} processing fee ({squareInfo.fee_percent}%)</span>
                <span className="text-sm text-[#B9BFCE]">+${processingFee.toFixed(2)}</span>
              </div>
            )}
            <div className="flex items-center justify-between border-t border-[#232B40] pt-3">
              <span className="text-xs uppercase tracking-[0.25em] text-[#8E96AB]">Order total</span>
              <span className="font-serif text-3xl text-ink" data-testid="order-total">
                ${checkoutTotal.toFixed(2)}
              </span>
            </div>
            {!isMember && memberSavings > 0.005 && (
              <Link to="/membership" className="block text-center text-[10px] uppercase tracking-[0.2em] text-clinical border border-[rgba(200,169,106,0.35)] rounded-xl px-4 py-3 hover:bg-clinical/5 transition-colors duration-300" data-testid="member-savings-teaser">
                Members save ${memberSavings.toFixed(2)} on this cart — join for $39.99/mo
              </Link>
            )}
            {isMember && memberSavings > 0.005 && (
              <p className="text-[10px] uppercase tracking-[0.2em] text-clinical" data-testid="member-savings-applied">
                Member pricing applied — you saved ${memberSavings.toFixed(2)}
              </p>
            )}
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8E96AB]">{COPY.shipping_note}</p>
          </div>
          {recommendations.length > 0 && (
            <div className="bg-paper p-6 space-y-4" data-testid="cart-recommendations">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]">Complete your research</p>
              {recommendations.map((p) => {
                const v = (p.variants || [])[0];
                const price = v ? v.price : p.price;
                return (
                  <div key={p.id} className="flex items-center justify-between gap-4" data-testid={`recommendation-${p.id}`}>
                    <div>
                      <p className="text-sm text-ink">{p.name}</p>
                      <p className="text-xs text-[#8E96AB]">{v ? v.label : p.size} — ${price}</p>
                    </div>
                    <button
                      data-testid={`recommendation-add-${p.id}`}
                      onClick={() => recAdd(p)}
                      className="text-[10px] uppercase tracking-[0.15em] border border-[rgba(200,169,106,0.28)] text-ink px-4 py-2 hover:bg-[#DBC28C] hover:text-cream hover:border-clinical transition-colors duration-300"
                    >
                      Add
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="border border-[#232B40] bg-surface/40 p-8 sm:p-12 space-y-8" data-testid="checkout-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]" htmlFor="co-first">First name *</label>
                <Input id="co-first" data-testid="checkout-first-name-input" value={form.first_name} onChange={update("first_name")} className="rounded-none border-[rgba(200,169,106,0.28)] bg-transparent focus-visible:ring-clinical h-12" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]" htmlFor="co-last">Last name *</label>
                <Input id="co-last" data-testid="checkout-last-name-input" value={form.last_name} onChange={update("last_name")} className="rounded-none border-[rgba(200,169,106,0.28)] bg-transparent focus-visible:ring-clinical h-12" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]" htmlFor="co-phone">Phone number *</label>
                <Input id="co-phone" type="tel" data-testid="checkout-phone-input" value={form.phone} onChange={update("phone")} className="rounded-none border-[rgba(200,169,106,0.28)] bg-transparent focus-visible:ring-clinical h-12" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]" htmlFor="co-email">E-mail address *</label>
                <Input id="co-email" type="email" data-testid="checkout-email-input" value={form.email} onChange={update("email")} className="rounded-none border-[rgba(200,169,106,0.28)] bg-transparent focus-visible:ring-clinical h-12" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]" htmlFor="co-street">Shipping address — street *</label>
              <Input id="co-street" data-testid="checkout-street-input" value={form.street} onChange={update("street")} className="rounded-none border-[rgba(200,169,106,0.28)] bg-transparent focus-visible:ring-clinical h-12" placeholder="123 Research Way, Apt 4" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]" htmlFor="co-city">City *</label>
                <Input id="co-city" data-testid="checkout-city-input" value={form.city} onChange={update("city")} className="rounded-none border-[rgba(200,169,106,0.28)] bg-transparent focus-visible:ring-clinical h-12" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]" htmlFor="co-state">State *</label>
                <Input id="co-state" data-testid="checkout-state-input" value={form.state} onChange={update("state")} className="rounded-none border-[rgba(200,169,106,0.28)] bg-transparent focus-visible:ring-clinical h-12" />
              </div>
              <div className="space-y-2 col-span-2 sm:col-span-1">
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]" htmlFor="co-zip">ZIP code *</label>
                <Input id="co-zip" data-testid="checkout-zip-input" value={form.zip_code} onChange={update("zip_code")} className="rounded-none border-[rgba(200,169,106,0.28)] bg-transparent focus-visible:ring-clinical h-12" />
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]">Shipping method *</p>
              <div className="grid grid-cols-2 gap-4">
                {["FedEx"].map((m) => (
                  <button
                    type="button"
                    key={m}
                    data-testid={`shipping-${m.toLowerCase()}-option`}
                    onClick={() => setShipping(m)}
                    className={`text-xs uppercase tracking-[0.2em] py-4 px-6 border transition-colors duration-300 ${
                      shipping === m
                        ? "border-clinical bg-clinical/10 text-clinical"
                        : "border-[rgba(200,169,106,0.28)] text-[#B9BFCE] hover:border-ink/40"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]">Referral / discount code</p>
              <div className="flex gap-3">
                <Input
                  data-testid="discount-code-input"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                  placeholder="Enter code"
                  className="rounded-none border-[rgba(200,169,106,0.28)] bg-transparent focus-visible:ring-clinical h-12 uppercase"
                />
                <button
                  type="button"
                  data-testid="discount-apply-button"
                  onClick={applyCode}
                  className="border border-[rgba(200,169,106,0.28)] text-ink text-xs uppercase tracking-[0.2em] px-6 hover:bg-[#1A233A] transition-colors duration-300 whitespace-nowrap"
                >
                  Apply
                </button>
              </div>
              {codeError && <p className="text-xs text-[#EF4444]" data-testid="discount-error">{codeError}</p>}
              {discount && (
                <p className="text-xs text-clinical" data-testid="discount-applied">
                  {discount.code} applied — {discount.percent}% off your order{" "}
                  <button type="button" data-testid="discount-remove-button" onClick={removeDiscount} className="underline underline-offset-2 text-[#8E96AB] hover:text-[#EF4444] transition-colors duration-300">remove</button>
                </p>
              )}
            </div>

            {hasHouseItems && (
            <div className="space-y-3">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]">
                {multiVendor ? "Payment — VetForge Supply items *" : "Payment method *"}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <button
                  type="button"
                  data-testid="payment-manual-option"
                  onClick={() => setPaymentMethod("manual")}
                  className={`text-left py-4 px-6 border transition-colors duration-300 space-y-1 ${
                    paymentMethod === "manual"
                      ? "border-clinical bg-clinical/10"
                      : "border-[rgba(200,169,106,0.28)] hover:border-ink/40"
                  }`}
                >
                  <span className={`block text-xs uppercase tracking-[0.2em] ${paymentMethod === "manual" ? "text-clinical" : "text-ink"}`}>Manual payment</span>
                  <span className={`block text-[10px] uppercase tracking-[0.15em] ${paymentMethod === "manual" ? "text-[#B9BFCE]" : "text-[#8E96AB]"}`}>Venmo · CashApp · Zelle · Crypto — no fee</span>
                </button>
                <button
                  type="button"
                  data-testid="payment-paypal-option"
                  onClick={() => setPaymentMethod("paypal")}
                  className={`text-left py-4 px-6 border transition-colors duration-300 space-y-1 ${
                    paymentMethod === "paypal"
                      ? "border-clinical bg-clinical/10"
                      : "border-[rgba(200,169,106,0.28)] hover:border-ink/40"
                  }`}
                >
                  <span className={`block text-xs uppercase tracking-[0.2em] ${paymentMethod === "paypal" ? "text-clinical" : "text-ink"}`}>PayPal</span>
                  <span className={`block text-[10px] uppercase tracking-[0.15em] ${paymentMethod === "paypal" ? "text-[#B9BFCE]" : "text-[#8E96AB]"}`}>Pay after confirmation — +{squareInfo.fee_percent}% processing fee</span>
                </button>
                <button
                  type="button"
                  data-testid="payment-square-option"
                  onClick={() => squareInfo.enabled && setPaymentMethod("square")}
                  disabled={!squareInfo.enabled}
                  className={`text-left py-4 px-6 border transition-colors duration-300 space-y-1 ${
                    paymentMethod === "square"
                      ? "border-clinical bg-clinical/10"
                      : "border-[rgba(200,169,106,0.28)] hover:border-ink/40"
                  } ${!squareInfo.enabled ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <span className={`block text-xs uppercase tracking-[0.2em] ${paymentMethod === "square" ? "text-clinical" : "text-ink"}`}>Card via Square</span>
                  <span className={`block text-[10px] uppercase tracking-[0.15em] ${paymentMethod === "square" ? "text-[#B9BFCE]" : "text-[#8E96AB]"}`}>
                    {squareInfo.enabled ? `Pay now by card — secure Square checkout, +${squareInfo.fee_percent}% fee` : "Card checkout coming soon"}
                  </span>
                </button>
              </div>
              <p className="text-[11px] text-[#B9BFCE] leading-relaxed border border-[#232B40] bg-paper px-4 py-3" data-testid="fee-notice">
                A <b>{squareInfo.fee_percent}% processing fee</b> applies to PayPal and all card payments on VetForge Supply items. Venmo, CashApp, Zelle, crypto, and all vendor-direct payments carry no fee.
              </p>
            </div>
            )}

            {paymentMethod === "manual" && hasHouseItems && (
            <div className="border border-[#232B40] bg-paper p-5 space-y-4" data-testid="checkout-payment-info">
              <p className="text-xs text-[#B9BFCE] leading-relaxed">
                {COPY.payment_note}
              </p>
              {(vendorOptions.house || []).length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]">Pay VetForge Supply via</p>
                  <select
                    data-testid="payment-choice-house"
                    value={paymentChoices.house || ""}
                    onChange={(e) => setPaymentChoices((prev) => ({ ...prev, house: e.target.value }))}
                    className="w-full border border-[#232B40] bg-[#0B101C] text-ink text-sm h-11 px-3 rounded-xl focus:outline-none focus:ring-1 focus:ring-clinical"
                  >
                    <option value="">Choose payment option…</option>
                    {(vendorOptions.house || []).map((o) => (
                      <option key={o.type} value={o.type}>{o.label}</option>
                    ))}
                  </select>
                  {paymentChoices.house && (vendorOptions.house || []).find((o) => o.type === paymentChoices.house) && (
                    <div className="flex items-center gap-3 border border-[rgba(200,169,106,0.35)] bg-[#0B101C] rounded-xl px-4 py-3" data-testid="payment-handle-house">
                      <span className="text-sm text-clinical font-mono break-all flex-1">{(vendorOptions.house || []).find((o) => o.type === paymentChoices.house).handle}</span>
                      <button type="button" data-testid="payment-copy-house" onClick={() => { navigator.clipboard?.writeText((vendorOptions.house || []).find((o) => o.type === paymentChoices.house).handle); toast.success("Payment address copied."); }} className="text-[10px] uppercase tracking-[0.15em] text-[#8E96AB] hover:text-clinical border border-[#232B40] rounded-lg px-3 py-1.5 transition-colors duration-300 shrink-0">Copy</button>
                    </div>
                  )}
                  {CRYPTO_TYPES.includes(paymentChoices.house) && <CryptoRouteHint testid="crypto-hint-house" />}
                </div>
              )}
              <p className="text-[11px] text-warning leading-relaxed uppercase tracking-[0.15em]" data-testid="payment-deadline-note">
                {COPY.deadline_note}
              </p>
            </div>
            )}

            {/* Each vendor store gets its OWN payment selector — carts never share one checkout */}
            {Object.entries(vendorGroups).filter(([k]) => k !== "house").map(([vKey, group]) => {
              const opts = vendorOptions[vKey];
              const chosen = paymentChoices[vKey];
              const chosenOpt = (opts || []).find((o) => o.type === chosen);
              return (
                <div key={vKey} className="border border-[#232B40] bg-paper p-5 space-y-4" data-testid={`vendor-payment-${vKey.slice(0, 8)}`}>
                  <div className="flex items-center gap-2">
                    <Store className="h-4 w-4 text-clinical" />
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]">Payment — {group.name} items *</p>
                  </div>
                  {opts === undefined ? (
                    <p className="text-xs text-[#8E96AB] inline-flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading this store's payment options…</p>
                  ) : opts.length === 0 ? (
                    <p className="text-xs text-[#B9BFCE] leading-relaxed" data-testid={`vendor-payment-none-${vKey.slice(0, 8)}`}>
                      {group.name} will email you payment instructions once your order is confirmed.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      <select
                        data-testid={`payment-choice-${vKey.slice(0, 8)}`}
                        value={chosen || ""}
                        onChange={(e) => setPaymentChoices((prev) => ({ ...prev, [vKey]: e.target.value }))}
                        className="w-full border border-[#232B40] bg-[#0B101C] text-ink text-sm h-11 px-3 rounded-xl focus:outline-none focus:ring-1 focus:ring-clinical"
                      >
                        <option value="">Choose how you'll pay {group.name}…</option>
                        {opts.map((o) => (
                          <option key={o.type} value={o.type}>{o.label}</option>
                        ))}
                      </select>
                      {chosenOpt && (
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 border border-[rgba(200,169,106,0.35)] bg-[#0B101C] rounded-xl px-4 py-3" data-testid={`payment-handle-${vKey.slice(0, 8)}`}>
                            <span className="text-sm text-clinical font-mono break-all flex-1">{chosenOpt.handle}</span>
                            <button type="button" data-testid={`payment-copy-${vKey.slice(0, 8)}`} onClick={() => { navigator.clipboard?.writeText(chosenOpt.handle); toast.success("Payment address copied."); }} className="text-[10px] uppercase tracking-[0.15em] text-[#8E96AB] hover:text-clinical border border-[#232B40] rounded-lg px-3 py-1.5 transition-colors duration-300 shrink-0">Copy</button>
                          </div>
                          {chosenOpt.qr && (
                            <div className="flex items-center gap-4" data-testid={`payment-qr-${vKey.slice(0, 8)}`}>
                              <img src={`${API}/vendors/${vKey}/payment-options/${chosenOpt.type}/qr`} alt={`Scan to pay ${chosenOpt.label}`} className="w-28 h-28 rounded-xl border border-[rgba(200,169,106,0.35)] bg-white object-contain p-1.5" />
                              <p className="text-[10px] uppercase tracking-[0.2em] text-[#8E96AB] leading-relaxed">Scan to pay<br />or type it manually above</p>
                            </div>
                          )}
                          {CRYPTO_TYPES.includes(chosenOpt.type) && <CryptoRouteHint testid={"crypto-hint-" + vKey.slice(0, 8)} />}
                        </div>
                      )}
                      <p className="text-[10px] text-[#8E96AB] leading-relaxed">Paid directly to {group.name} — no VetForge processing fee. The store is notified to watch for your payment.</p>
                    </div>
                  )}
                </div>
              );
            })}
            {paymentMethod === "paypal" && (
              <div className="border border-[#232B40] bg-paper p-5 space-y-2" data-testid="checkout-paypal-info">
                <p className="text-xs text-[#B9BFCE] leading-relaxed">
                  After VetForge confirms your order and stock, we'll email you a PayPal payment request for <b>${checkoutTotal.toFixed(2)}</b> — your total includes the {squareInfo.fee_percent}% PayPal processing fee (+${processingFee.toFixed(2)}).
                </p>
                <p className="text-[11px] text-warning leading-relaxed uppercase tracking-[0.15em]">
                  {COPY.deadline_note}
                </p>
              </div>
            )}
            {paymentMethod === "square" && (
              <div className="border border-[#232B40] bg-paper p-5 space-y-2" data-testid="checkout-square-info">
                <p className="text-xs text-[#B9BFCE] leading-relaxed">
                  After placing your order you'll be redirected to Square's secure checkout to pay by card. A {squareInfo.fee_percent}% card processing fee (+${processingFee.toFixed(2)}) is added to your total.
                </p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8E96AB]">Your order and stock availability are confirmed by VetForge before dispatch.</p>
              </div>
            )}

            <div className="border border-[#232B40] bg-paper p-5" data-testid="checkout-policy-note">
              <p className="text-[11px] text-[#B9BFCE] leading-relaxed">
                <span className="text-ink uppercase tracking-[0.15em]">All sales are final.</span>{" "}
                It is the goal of VetForge Health and Wellness to provide a world class experience. If we do not meet those expectations with an order issue, please allow us the opportunity to reconcile the situation.
              </p>
            </div>

            <button
              type="submit"
              data-testid="submit-order-button"
              disabled={submitting}
              className="group inline-flex items-center gap-3 bg-clinical text-[#0B101C] text-xs uppercase tracking-[0.2em] py-4 px-10 hover:bg-[#DBC28C] transition-colors duration-300 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Submitting
                </>
              ) : (
                <>
                  {paymentMethod === "square" ? "Place order & pay by card" : "Submit order"}
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
